"use client";

/**
 * P3-08 · Add-to-cart button.
 *
 * The primary buy CTA. Disabled until a valid variant is selected and in stock;
 * while disabled its label becomes the reason ("Select a size" / "Out of
 * stock"). On click it fires the optimistic add; the pending state is announced
 * via `aria-busy` + an `sr-only` live region for assistive tech.
 */
import type { CartPersonalisation } from "~/lib/cart/cart-interface";
import { cn } from "~/lib/utils";

import { useAddToCart, type AddToCartSource } from "./use-add-to-cart";
import { useTranslator } from "~/i18n/provider";

export function AddToCartButton({
  variantId,
  quantity,
  personalisation,
  disabled,
  reason,
  label,
  source = "pdp",
  testId = "pdp-add-to-cart",
  className,
  onAdded,
}: {
  variantId: string;
  quantity: number;
  personalisation: CartPersonalisation;
  disabled: boolean;
  reason: string | null;
  label?: string;
  source?: AddToCartSource;
  testId?: string;
  className?: string;
  onAdded?: () => void;
}) {
  const t = useTranslator();
  const { add, isAdding } = useAddToCart({
    variantId,
    quantity,
    personalisation,
  });

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        disabled={disabled || isAdding}
        aria-busy={isAdding}
        onClick={async () => {
          const ok = await add(source);
          if (ok) onAdded?.();
        }}
        className={cn(
          "w-full rounded-[var(--btn-radius)] bg-primary px-4 py-3 text-sm text-primary-fg [font-weight:var(--btn-weight)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        {isAdding
          ? t("pdp.adding")
          : disabled && reason
            ? reason
            : (label ?? t("pdp.addToCart"))}
      </button>
      <span role="status" aria-live="polite" className="sr-only">
        {isAdding ? "Adding to bag" : ""}
      </span>
    </>
  );
}
